import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { Button2Component } from '@frontendlib/components/components/button/button2.component';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { BaseModalComponent } from '@modules/modal/components/base-modal/base-modal.component';
import { BoardListStore } from '../../../services/board-list.store';
import { BoardsStore } from '../../../services/boards.store';
import { type Board } from '../../../types/board';
import { injectBoardOpener } from '../../shared/board-opener';

/**
 * "From a template": the boards marked as templates, each choosing to a copy in the folder. Adapted from
 * `DeckCreateOptionsComponent`; the copy is the server's duplicate (filed in the folder by it) and the list is the
 * store's own, so nothing is fetched twice. A retried choice of the same template reuses the copy already made.
 */
@Component({
  selector: 'app-board-create-options',
  templateUrl: './board-create-options.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Button2Component],
})
export class BoardCreateOptionsComponent extends BaseModalComponent {
  public folderId: string | null = null;
  public onCreated?: (id: string) => void;
  protected readonly busy = signal(false);
  protected readonly error = signal('');
  private readonly store = inject(BoardsStore);
  private readonly list = inject(BoardListStore, { optional: true });
  private readonly openBoard = injectBoardOpener();
  private readonly context = inject(SpaceContextStore);
  private readonly initialSpace = this.context.contextId();
  protected readonly templates = computed<Board[]>(() => this.store.boards().filter((board) => board.isTemplate && !board.deleted && !board.archived));
  protected readonly loading = computed(() => this.store.boardsLoading() && !this.store.boardsLoaded());

  public constructor() {
    super();
    effect(() => {
      if (this.context.contextId() !== this.initialSpace) this.close();
    });
  }

  protected choose(template: Board): void {
    if (this.busy() || this.context.contextId() !== this.initialSpace) return;
    this.busy.set(true);
    this.error.set('');
    // `null` = no folder; the server files the copy where it is told, or in the template's own folder by default.
    this.store.duplicateBoard(template.id, this.folderId).subscribe((board) => {
      this.busy.set(false);
      if (this.context.contextId() !== this.initialSpace) return;
      if (!board) {
        this.error.set(
          this.store.hasUnfinishedCopy(template.id, this.folderId)
            ? 'The board was made but could not be finished. Choose the same template again to finish it; no second board is made.'
            : 'Could not create a board from this template. Try again.',
        );
        return;
      }
      this.list?.ensureVisibleAfterCreate(board.id);
      if (this.onCreated) this.onCreated(board.id);
      else this.openBoard(board.id);
      this.close();
    });
  }
}
